/**
 * UnsplashPicker — Modal photo picker for the SocialPulse TipTap editor.
 *
 * Search Unsplash → click a thumbnail → returns the picked photo
 * (full metadata including required attribution links) to the caller.
 *
 * The caller is responsible for actually inserting the image node and
 * then firing `/unsplash/track-use` so Unsplash credits the photographer.
 * We call track-use here on commit so every consumer stays compliant.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Search, X, Loader2, ExternalLink } from "lucide-react";

const BASE = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

export default function UnsplashPicker({
  open,
  onClose,
  onPick,
  initialQuery = "",
  context = "publisher_editor",
}) {
  const [query, setQuery] = useState(initialQuery);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [committing, setCommitting] = useState(null); // photo_id being committed
  const inputRef = useRef(null);

  const runSearch = useCallback(async (q) => {
    const term = (q || "").trim();
    if (!term) {
      setResults([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const r = await fetch(
        `${BASE}/unsplash/search?q=${encodeURIComponent(term)}&per_page=18&orientation=landscape`,
      );
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      setResults(data.results || []);
    } catch (e) {
      setError(e.message || "Search failed");
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Auto-run when opened with an initial query, and focus the input
  useEffect(() => {
    if (!open) return;
    setQuery(initialQuery);
    setError(null);
    if (initialQuery) runSearch(initialQuery);
    // Focus after paint
    setTimeout(() => inputRef.current?.focus(), 50);
  }, [open, initialQuery, runSearch]);

  const handleSubmit = (e) => {
    e.preventDefault();
    runSearch(query);
  };

  const commit = useCallback(
    async (photo) => {
      setCommitting(photo.photo_id);
      // Fire-and-forget track-use (Unsplash API guideline requirement)
      fetch(`${BASE}/unsplash/track-use`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          photo_id: photo.photo_id,
          context,
          download_location: photo.download_location,
        }),
      }).catch(() => {});
      onPick?.(photo);
      setCommitting(null);
      onClose?.();
    },
    [onPick, onClose, context],
  );

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-start justify-center bg-slate-900/70 backdrop-blur-sm p-4 overflow-y-auto"
      data-testid="unsplash-picker-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div
        className="w-full max-w-5xl mt-8 bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col"
        data-testid="unsplash-picker-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">
              Photo library
            </div>
            <div className="text-lg font-bold text-slate-900">Unsplash</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-slate-100 transition-colors"
            data-testid="unsplash-picker-close"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Search bar */}
        <form onSubmit={handleSubmit} className="px-6 py-4 border-b border-slate-100">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search Unsplash — try 'mountain trail', 'coffee shop', 'farm sunrise'…"
              className="w-full pl-10 pr-24 py-2.5 rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              data-testid="unsplash-picker-input"
            />
            <button
              type="submit"
              disabled={loading || !query.trim()}
              className="absolute right-1.5 top-1/2 -translate-y-1/2 px-3 py-1.5 bg-slate-900 text-white text-xs font-semibold rounded-lg hover:bg-slate-700 disabled:opacity-40 transition"
              data-testid="unsplash-picker-search"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Search"}
            </button>
          </div>
          {error && (
            <div className="mt-2 text-xs text-red-600" data-testid="unsplash-picker-error">
              {error}
            </div>
          )}
        </form>

        {/* Results grid */}
        <div className="p-6 min-h-[300px] max-h-[60vh] overflow-y-auto">
          {loading && results.length === 0 && (
            <div className="flex items-center justify-center py-16 text-slate-500">
              <Loader2 className="w-6 h-6 animate-spin mr-2" /> Searching Unsplash…
            </div>
          )}
          {!loading && results.length === 0 && !error && (
            <div className="text-center py-16 text-slate-400 text-sm">
              {query.trim()
                ? "No results yet — press Search."
                : "Type a search term above to browse Unsplash."}
            </div>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {results.map((p) => (
              <button
                key={p.photo_id}
                type="button"
                onClick={() => commit(p)}
                disabled={committing === p.photo_id}
                className="group relative rounded-lg overflow-hidden bg-slate-100 aspect-[4/3] focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                data-testid={`unsplash-photo-${p.photo_id}`}
                style={{ backgroundColor: p.color || "#e2e8f0" }}
              >
                <img
                  src={p.thumb_url}
                  alt={p.alt_description || ""}
                  loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
                {committing === p.photo_id && (
                  <div className="absolute inset-0 bg-slate-900/60 flex items-center justify-center">
                    <Loader2 className="w-6 h-6 text-white animate-spin" />
                  </div>
                )}
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent px-2 py-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="text-[10px] text-white/90 truncate">
                    Photo by {p.photographer?.name}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Footer — attribution reminder */}
        <div className="px-6 py-3 border-t border-slate-100 bg-slate-50 flex items-center justify-between text-xs text-slate-500">
          <span>
            Photos courtesy of{" "}
            <a
              href="https://unsplash.com/?utm_source=social_pulse&utm_medium=referral"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-slate-900"
            >
              Unsplash
            </a>
            . Photographer credit is embedded automatically.
          </span>
          <a
            href="https://unsplash.com/license"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 hover:text-slate-900"
          >
            License <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      </div>
    </div>
  );
}
